import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { chatApi, userApi } from '../api/endpoints.js';
import { useRealtime, useRealtimeEvent } from '../hooks/useRealtime.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../hooks/useToast.jsx';
import { useApiError } from '../hooks/useApiError.js';
import PageHeader from '../components/PageHeader.jsx';
import Spinner from '../components/Spinner.jsx';
import Modal from '../components/Modal.jsx';
import { Field, TextArea, TextInput } from '../components/Field.jsx';
import PeoplePicker from '../components/PeoplePicker.jsx';
import { AttachmentList, AttachmentPicker } from '../components/Attachments.jsx';
import { RichText, RichTextToolbar } from '../components/RichText.jsx';
import { formatDateTime, fullName } from '../utils/format.js';

/**
 * Live chat between colleagues.
 *
 * Messages are sent over the normal REST API and arrive over a Server-Sent
 * Events stream, so a message appears on the other screen the moment it is
 * written - no polling, and the browser reconnects the stream on its own if the
 * network blinks.
 *
 * It complements the comment threads rather than replacing them: anything that
 * belongs to a customer's history stays on the customer, where the next person
 * to open the account will find it.
 */
const ChatPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [rooms, setRooms] = useState([]);
  const [current, setCurrent] = useState(null);
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState([]);
  const draftRef = useRef(null);

  const [picking, setPicking] = useState(false);
  const [invited, setInvited] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const { connected: live } = useRealtime();
  const bottomRef = useRef(null);
  const currentRef = useRef(null);
  currentRef.current = current;

  const { data: directory } = useApi(() => userApi.directory(), []);

  const loadRooms = async () => {
    try {
      const data = await chatApi.conversations();
      setRooms(data.items ?? []);
      return data.items ?? [];
    } catch (err) {
      toast.error(toMessage(err));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const openRoom = async (room) => {
    setCurrent(room);
    try {
      const data = await chatApi.messages(room.id);
      setThread(data.items ?? []);
      await chatApi.markRead(room.id);
      setRooms((list) => list.map((item) => (item.id === room.id ? { ...item, unread: 0 } : item)));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  useEffect(() => {
    loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The shared stream does the connecting and reconnecting; this only has to
     decide what a new message means for what is on screen. */
  useRealtimeEvent('message', (message) => {
      if (currentRef.current?.id === message.conversationId) {
        setThread((list) => (list.some((item) => item.id === message.id) ? list : [...list, message]));
        chatApi.markRead(message.conversationId).catch(() => {});
      }

      setRooms((list) => {
        const known = list.some((room) => room.id === message.conversationId);
        // A brand new conversation: pull the list again rather than guess it.
        if (!known) {
          loadRooms();
          return list;
        }
        return list
          .map((room) =>
            room.id === message.conversationId
              ? {
                  ...room,
                  lastMessage: { body: message.body, createdAt: message.createdAt, userId: message.userId },
                  unread:
                    currentRef.current?.id === room.id || message.userId === user.id
                      ? 0
                      : (room.unread ?? 0) + 1,
                }
              : room,
          )
          .sort((a, b) => ((a.lastMessage?.createdAt ?? '') < (b.lastMessage?.createdAt ?? '') ? 1 : -1));
      });
  });

  // Arriving from a notification: /chat?c=<id> opens that room straight away.
  useEffect(() => {
    const wanted = searchParams.get('c');
    if (!wanted || current?.id === wanted) return;
    const room = rooms.find((item) => item.id === wanted);
    if (room) {
      openRoom(room);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, searchParams]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [thread]);

  const send = async () => {
    const body = draft.trim();
    if ((!body && !files.length) || !current) return;
    setDraft('');
    try {
      // The stream delivers it back to us, so nothing is appended here.
      await chatApi.send(current.id, body, files.map((file) => file.id));
      setFiles([]);
    } catch (err) {
      setDraft(body);
      toast.error(toMessage(err));
    }
  };

  const startConversation = async () => {
    if (!invited.length) return;
    setPicking(false);
    try {
      const room = await chatApi.open(invited, groupName);
      const list = await loadRooms();
      await openRoom(list.find((item) => item.id === room.id) ?? room);
      setInvited([]);
      setGroupName('');
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const colleagues = useMemo(
    () => (directory?.items ?? []).filter((person) => person.id !== user.id),
    [directory, user.id],
  );

  if (loading) return <Spinner full />;

  return (
    <>
      <PageHeader
        title={t('chat.title')}
        subtitle={t('chat.subtitle')}
        actions={
          <div className="header-actions">
            <span className={live ? 'chat__live chat__live--on' : 'chat__live'}>
              {live ? t('chat.live') : t('chat.offline')}
            </span>
            <button type="button" className="btn btn--primary" onClick={() => setPicking(true)}>
              {t('chat.newConversation')}
            </button>
          </div>
        }
      />

      <div className="chat">
        <aside className="chat__rooms">
          {!rooms.length ? <p className="muted chat__empty">{t('chat.noConversation')}</p> : null}
          <ul>
            {rooms.map((room) => (
              <li key={room.id}>
                <button
                  type="button"
                  className={current?.id === room.id ? 'chat__room chat__room--on' : 'chat__room'}
                  onClick={() => openRoom(room)}
                >
                  <span className="chat__room-name">
                    {room.kind === 'GROUP' ? '# ' : ''}
                    {room.title || room.members.map((person) => fullName(person)).join(', ')}
                  </span>
                  <span className="chat__room-last">{room.lastMessage?.body ?? ''}</span>
                  {room.unread ? <span className="chat__unread">{room.unread}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="chat__thread">
          {!current ? (
            <p className="muted chat__empty">{t('chat.pick')}</p>
          ) : (
            <>
              <div className="chat__messages">
                {thread.map((message) => (
                  <div
                    key={message.id}
                    className={message.userId === user.id ? 'bubble bubble--mine' : 'bubble'}
                  >
                    {message.userId !== user.id ? (
                      <span className="bubble__author">{message.author?.firstName}</span>
                    ) : null}
                    <RichText text={message.body} className="bubble__body rich" />
                    <AttachmentList files={message.attachments} />
                    <span className="bubble__time">{formatDateTime(message.createdAt, locale)}</span>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="chat__composer">
                {/* A textarea rather than one line: formatting needs somewhere to
                    put a second line. Enter still sends; Shift+Enter breaks. */}
                <div className="chat__composer-box">
                  <RichTextToolbar inputRef={draftRef} value={draft} onChange={setDraft} />
                  <TextArea
                    ref={draftRef}
                    rows={2}
                    value={draft}
                    onChange={setDraft}
                    placeholder={t('chat.placeholder')}
                    aria-label={t('chat.placeholder')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        send();
                      }
                    }}
                  />
                </div>
                <AttachmentPicker value={files} onChange={setFiles} />
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={send}
                  disabled={!draft.trim() && !files.length}
                >
                  {t('chat.send')}
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      <Modal
        open={picking}
        title={t('chat.newConversation')}
        onClose={() => setPicking(false)}
        width={460}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setPicking(false)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={startConversation} disabled={!invited.length}>
              {invited.length > 1 ? t('chat.createGroup') : t('chat.start')}
            </button>
          </>
        }
      >
        <Field label={t('chat.withWho')}>
          <PeoplePicker people={colleagues} value={invited} onChange={setInvited} multiple autoFocus />
        </Field>

        {/* A name only matters once there are several people in the room. */}
        {invited.length > 1 ? (
          <Field label={t('chat.groupName')} htmlFor="group-name">
            <TextInput
              id="group-name"
              value={groupName}
              onChange={setGroupName}
              placeholder={t('chat.groupNamePlaceholder')}
            />
          </Field>
        ) : null}
      </Modal>
    </>
  );
};

export default ChatPage;
